import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Input, Modal, Select, Spin, Table, Tag, Tooltip, message } from "antd";
import {
  CountChip,
  FilterCard,
  FilterField,
  MonoCell,
  PageHeader,
  PageShell,
  RowActionMenu,
  StatusPill,
  TableCard,
  anTableProps,
  fmtDate,
  tableEmpty,
} from "../../../design-system/tableUi";
import { Link } from "react-router-dom";
import { AlertTriangle, CalendarClock, KeyRound, Loader, Search } from "lucide-react";
import axios from "axios";
import moment from "moment-timezone";
import {
  AUTH_TOKEN,
  REACT_APP_PAM_API,
  ORG_ID,
  DOMAIN_ID,
} from "../../../constants";
import { logOutUser } from "../../../common";
import { useDebounce } from "../../../common/debounce";
import { AN_COLORS, AN_MONO } from "../../../design-system/tokens";
import { SourceTag } from "../endpoints/setupScan";
import {
  endpointLifecycle,
  listInstances,
  listLocalUsers,
  listManagedAccounts,
  listPasswordAccounts,
  passwordErrorText,
  TEMP_ACCOUNT,
} from "../../../service/sshProxyService";
import { isAdminUser } from "../../../utils/helpers";
import AccountRotationModal from "../../../pages/enduser/password-rotation/AccountRotationModal";
import RevealPasswordModal from "../../../pages/enduser/password-rotation/RevealPasswordModal";
import { useOpenEndpointSetup } from "../../../pages/enduser/password-rotation/passwordUi";
import { accountKey } from "../../../pages/enduser/password-rotation/accountCells";

const PAGE_SIZE = 10;
// While a Rotate now runs, re-read the accounts every 5 s, for at most 2 min.
const POLL_MS = 5000;
const POLL_MAX_MS = 2 * 60 * 1000;

// The endpoints a row's account lives on. listAllEpmUsers returns
// `instances`; epmMachineId is the legacy single-machine field.
const rowInstanceIds = (u) =>
  Array.from(
    new Set(
      (u.instances || [])
        .map((i) => i.instanceId)
        .concat(u.epmMachineId ? [u.epmMachineId] : [])
        .filter(Boolean)
        .map(String),
    ),
  );

// Temporary users (one account per session, removed when it ends) are not
// shown: the reserved "temp" account and the per-session "an-…" accounts the
// helper creates for it.
const isTempAccount = (name) => {
  const n = String(name || "").toLowerCase();
  return n === TEMP_ACCOUNT || n.startsWith("an-");
};

// Accounts whose password Authnull never rotates. root is allowed (its own
// rotation keeps it break-glass only).
const NOT_ROTATABLE = ["authnull-svc", "nobody"];
const rotatable = (name) => !NOT_ROTATABLE.includes(String(name || "").toLowerCase());

// Unix seconds (number or numeric string) → number, or null when unknown.
const unixOrNull = (v) => (Number(v) > 0 ? Number(v) : null);

// --- presentational helpers (kept in sync with Users) ---



// Last-active → a StatusPill tone and a one-word label; the age is in the
// tooltip. Thresholds unchanged: >20m offline, >10m stale, otherwise online.
const activityOf = (lastActive) => {
  if (lastActive == null) return { tone: "neutral", label: "Unknown", title: "No activity reported" };
  const tz = localStorage.getItem("timezone") || moment.tz.guess();
  const at = moment.unix(lastActive).tz(tz);
  const diffMin = Math.max(0, moment().diff(at, "minutes"));

  let ago;
  if (diffMin < 1) ago = "<1m";
  else if (diffMin < 60) ago = `${diffMin}m`;
  else if (diffMin < 60 * 24) ago = `${Math.floor(diffMin / 60)}h`;
  else ago = `${Math.floor(diffMin / 60 / 24)}d`;

  const tone = diffMin > 20 ? "danger" : diffMin > 10 ? "warn" : "success";
  const name = diffMin > 20 ? "Offline" : diffMin > 10 ? "Stale" : "Online";
  return { tone, label: name, title: `Last active ${at.format("YYYY-MM-DD HH:mm")} (${ago} ago)` };
};

// Rotation as "Every N days" or "Off", with a failed / rotating line under it;
// shared profile, next rotation and failure detail in the tooltip. A helper too
// old for rotation shows "Needs setup", which opens the endpoint's setup;
// otherwise, where onOpen is given, the cell opens Password rotation… for the
// account.
const DAY_MS = 24 * 60 * 60 * 1000;
// "Every 30 days": from the account's own setting when read (rot, from
// listLocalUsers), else from its last and next rotation when they are a whole
// number of days apart.
function scheduleText(pw, rot) {
  let days = rot && rot.enabled ? Number(rot.intervalDays) || 0 : 0;
  if (!days && pw.rotatedAt && pw.nextDueAt && !pw.rerotateAt) {
    const d = (new Date(pw.nextDueAt).getTime() - new Date(pw.rotatedAt).getTime()) / DAY_MS;
    if (d >= 1 && Math.abs(d - Math.round(d)) < 0.05) days = Math.round(d);
  }
  return days ? `Every ${days} day${days === 1 ? "" : "s"}` : "";
}
// A shared profile's name; an account's own setting is stored as a profile
// named "<account> on <host>", which isn't one.
function sharedProfileName(pw, rot) {
  if (rot && rot.enabled) return rot.own ? "" : rot.profile || "";
  return pw.policyName && pw.policyName !== `${pw.account} on ${pw.hostname}` ? pw.policyName : "";
}

// A pill as a button that opens Password rotation… (or the endpoint's setup).
const pillButton = (pill, onClick) => (
  <button type="button" onClick={onClick} style={{ border: 0, padding: 0, background: "none", cursor: "pointer" }}>
    {pill}
  </button>
);

function RotationCell({ pw, rot, onOpen, onOpenSetup }) {
  // No password-accounts row means Authnull has never rotated it: Off too.
  if (!pw || !pw.policyId) {
    const off = <span style={{ fontSize: 12.5, color: AN_COLORS.muted, whiteSpace: "nowrap" }}>Off</span>;
    const why = pw ? "Rotation is off. The last password stays in the vault." : "Rotation is off.";
    return (
      <Tooltip title={`${why}${onOpen ? " Click to change." : ""}`}>
        <span style={{ display: "inline-flex" }}>{onOpen ? pillButton(off, onOpen) : off}</span>
      </Tooltip>
    );
  }
  if (pw.needsSetup) {
    return (
      <Tooltip title="The endpoint needs Re-run setup — click to open it.">
        <span style={{ display: "inline-flex" }}>
          {pillButton(<StatusPill tone="warn" label="Needs setup" />, () => onOpenSetup && onOpenSetup(pw.endpointId))}
        </span>
      </Tooltip>
    );
  }
  const profile = sharedProfileName(pw, rot);
  const failed = pw.status === "failed";
  const tip = [
    profile && `Profile: ${profile}`,
    pw.status === "managed" && pw.rotatedAt && `Last rotated ${fmtDate(pw.rotatedAt)}`,
    pw.status === "pending" && "Waiting for the first rotation.",
    failed && (pw.detail || "The last rotation failed."),
    pw.status === "no_password" && "The account has no password; Authnull doesn't give it one.",
    pw.rerotateAt
      ? `Re-rotates at ${fmtDate(pw.rerotateAt)} (after a reveal)`
      : pw.nextDueAt && pw.status !== "no_password" && `Next rotation ${fmtDate(pw.nextDueAt)}`,
    onOpen && "Click to change.",
  ].filter(Boolean);
  // "Every N days" — or "On" when the schedule can't be told from what was read.
  const label = <span style={{ fontSize: 12.5, color: AN_COLORS.ink2, whiteSpace: "nowrap" }}>{scheduleText(pw, rot) || "On"}</span>;
  return (
    <Tooltip
      title={
        tip.length ? (
          <div style={{ display: "grid", gap: 2 }}>
            {tip.map((t) => (
              <span key={t}>{t}</span>
            ))}
          </div>
        ) : undefined
      }
    >
      <div style={{ display: "grid", gap: 3, justifyItems: "start" }}>
        {onOpen ? pillButton(label, onOpen) : label}
        {failed && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11.5, color: AN_COLORS.danger, whiteSpace: "nowrap" }}>
            <AlertTriangle size={12} style={{ flexShrink: 0 }} />
            {pw.failures > 1 ? `Rotation failed ×${pw.failures}` : "Rotation failed"}
          </span>
        )}
        {pw.jobRunning && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11.5, color: AN_COLORS.muted, whiteSpace: "nowrap" }}>
            <Loader size={12} /> Rotating…
          </span>
        )}
      </div>
    </Tooltip>
  );
}

// One line per endpoint when an account row spans several, each led by the
// host; a single endpoint renders its cell alone.
function PerEndpoint({ lines, render }) {
  if (lines.length <= 1) return render(lines[0]?.pw, lines[0]);
  return (
    <div style={{ display: "grid", gap: 8 }}>
      {lines.map((l) => (
        <div key={l.instanceId} style={{ display: "grid", gap: 2 }}>
          <span style={{ fontFamily: AN_MONO, fontSize: 10.5, color: AN_COLORS.muted, whiteSpace: "nowrap" }}>{l.hostName}</span>
          {render(l.pw, l)}
        </div>
      ))}
    </div>
  );
}

const EndpointUsers = () => {
  const [notification, notificationContext] = message.useMessage();

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 600);

  const [endpointFilterId, setEndpointFilterId] = useState("");
  const [sortBy, setSortBy] = useState("default");

  const [pageIndex, setPageIndex] = useState(1); // 1-indexed
  const [totalCount, setTotalCount] = useState(0);
  // The page was full, so there may be another one even when the API gave no
  // totalCount.
  const [pageFull, setPageFull] = useState(false);
  const [epmUsers, setEpmUsers] = useState([]);
  const [endpointData, setEndpointData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Password rotation (administrators only): every rotated account, read once
  // with listPasswordAccounts({ instanceId: 0 }) and joined to the rows on
  // (endpoint, account). Re-read after a Rotate now or a reveal.
  const isAdmin = isAdminUser();
  const [pwAccounts, setPwAccounts] = useState(() => new Map()); // accountKey → account
  const [pwTick, setPwTick] = useState(0);
  const [revealing, setRevealing] = useState(null);
  const openSetup = useOpenEndpointSetup(notification);
  const pollStart = useRef(0);

  useEffect(() => {
    if (!isAdmin) return undefined;
    let live = true;
    listPasswordAccounts({
      instanceId: 0,
      policyId: "",
      onSuccess: (res) => {
        if (!live) return;
        setPwAccounts(new Map((res.accounts || []).map((a) => [accountKey(a.endpointId, a.account), a])));
      },
      onFailure: (err) => {
        if (!live) return;
        notification.error({ content: passwordErrorText(err, "Couldn't read the accounts' password rotation.") });
      },
    });
    return () => {
      live = false;
    };
  }, [isAdmin, pwTick, notification]);

  // Managed accounts (contractors and vendors): the active ones, by
  // (endpoint, account), for the "Managed · expires …" badge.
  const [managed, setManaged] = useState(() => new Map());
  useEffect(() => {
    if (!isAdmin) return undefined;
    let live = true;
    listManagedAccounts({
      instanceId: 0,
      onSuccess: (res) =>
        live &&
        setManaged(
          new Map(
            (res.managedAccounts || [])
              .filter((m) => m.status === "active")
              .map((m) => [accountKey(m.endpointId, m.account), m]),
          ),
        ),
      onFailure: () => {},
    });
    return () => {
      live = false;
    };
  }, [isAdmin]);

  // Password rotation… for one account on one endpoint: its current setting
  // is read from the endpoint's local users first. rotInfo keeps what was read
  // (accountKey → rotation) for the Rotation column's tooltip.
  const [rotTarget, setRotTarget] = useState(null); // { endpointId, hostName, account, loading, rotation, defaultIntervalDays }
  const [rotInfo, setRotInfo] = useState(() => new Map());
  const rotReq = useRef(0);
  const openRotation = (endpointId, hostName, account) => {
    const req = ++rotReq.current;
    setRotTarget({ endpointId, hostName, account, loading: true });
    listLocalUsers({
      instanceId: Number(endpointId),
      onSuccess: (res) => {
        if (req !== rotReq.current) return;
        const users = res.users || [];
        setRotInfo((m) => {
          const next = new Map(m);
          users.forEach((u) => next.set(accountKey(endpointId, u.account), u.rotation || {}));
          return next;
        });
        const r = users.find((u) => u.account === account)?.rotation;
        setRotTarget((t) =>
          t && {
            ...t,
            loading: false,
            rotation: { enabled: !!r?.enabled, intervalDays: Number(r?.intervalDays) || undefined, own: r?.own, profile: r?.profile, jobRunning: !!r?.jobRunning },
            defaultIntervalDays: Number(res.defaultIntervalDays) || 30,
          },
        );
      },
      onFailure: (err) => {
        if (req !== rotReq.current) return;
        setRotTarget(null);
        notification.error({ content: passwordErrorText(err, "Couldn't read the account's password rotation.") });
      },
    });
  };
  const closeRotation = () => {
    rotReq.current += 1;
    setRotTarget(null);
  };

  // Follow running rotations.
  useEffect(() => {
    if (![...pwAccounts.values()].some((a) => a.jobRunning)) {
      pollStart.current = 0;
      return undefined;
    }
    if (!pollStart.current) pollStart.current = Date.now();
    if (Date.now() - pollStart.current > POLL_MAX_MS) return undefined;
    const t = setTimeout(() => setPwTick((x) => x + 1), POLL_MS);
    return () => clearTimeout(t);
  }, [pwAccounts]);

  // A row's account on each of its endpoints: [{ instanceId, hostName,
  // gatewayManaged, pw, rot }], pw undefined where it has never been rotated,
  // rot its rotation as last read by Password rotation….
  const pwLines = useCallback(
    (u) => {
      const eps = (u.endpoints || []).length
        ? u.endpoints
        : rowInstanceIds(u).map((id) => ({
            instanceId: id,
            hostName: (u.instances || []).find((i) => String(i.instanceId) === id)?.hostname || `Endpoint #${id}`,
          }));
      return eps.map((e) => ({
        instanceId: e.instanceId,
        hostName: e.hostName,
        gatewayManaged: !!e.gatewayManaged,
        pw: pwAccounts.get(accountKey(e.instanceId, u.userName)),
        rot: rotInfo.get(accountKey(e.instanceId, u.userName)),
      }));
    },
    [pwAccounts, rotInfo],
  );



  const sortedUsers = useMemo(() => {
    if (sortBy === "default") return epmUsers;
    const arr = epmUsers.map((u) => ({ ...u, endpointSort: (u.endpoints || [])[0]?.hostName || u.epmMachineName || "" }));
    const keyMap = {
      userName: "userName",
      endpoint: "endpointSort",
    };
    arr.sort((a, b) => {
      const k = keyMap[sortBy];
      return String(a[k] || "").localeCompare(
        String(b[k] || ""),
        undefined,
        { numeric: true, sensitivity: "base" }
      );
    });
    return arr;
  }, [epmUsers, sortBy]);

  // Reset page when search or endpoint filter changes
  useEffect(() => {
    setPageIndex(1);
  }, [debouncedSearch, endpointFilterId]);

  // ---- API: list endpoint users (server pagination) ----
  const fetchEPMUsers = () => {
    const payload = {
      domainId: DOMAIN_ID(),
      pageId: pageIndex,
      pageSize: PAGE_SIZE,
      token: AUTH_TOKEN(),
      instanceIds: endpointFilterId ? [endpointFilterId] : [],
      filter: {
        filterBy: endpointFilterId ? "endpoints" : "",
        Value: "",
      },
      search: debouncedSearch,
      orgId: ORG_ID(),
    };
    setIsLoading(true);
    axios
      .post(`${REACT_APP_PAM_API}/endpointUsers/listAllEpmUsers`, payload, {
        headers: {
          withCredentials: true,
          "X-Authorization": AUTH_TOKEN(),
        },
      })
      .then((res) => {
        setIsLoading(false);
        const original = res?.data?.epmUsers || [];
        // Temporary users are hidden; the count drops this page's too.
        const shown = original.filter((u) => !isTempAccount(u.userName));
        const seeded = shown.map((u, i) => ({
          ...u,
          id: i + 1,
          lastActive: null,
        }));
        setEpmUsers(seeded);
        // The API may leave totalCount out: count the rows then.
        const apiTotal = Number(res?.data?.totalCount) || 0;
        const hidden = original.length - shown.length;
        const atLeast = (pageIndex - 1) * PAGE_SIZE + shown.length;
        setTotalCount(apiTotal ? Math.max(apiTotal - hidden, atLeast) : atLeast);
        setPageFull(!apiTotal && original.length >= PAGE_SIZE);

        // Enrich each row with its endpoints (addresses, gateway and
        // last-active from the endpoint list).
        new Promise((resolve) => listInstances({ onSuccess: resolve, onFailure: () => resolve([]) }))
          .then((endpoints) => {
            const endpointMap = new Map((endpoints || []).map((e) => [String(e.instanceId), e]));
            const enriched = seeded.map((u) => {
              const ids = rowInstanceIds(u).map(String);
              const endpointsOfRow = ids.map((id) => {
                const e = endpointMap.get(id);
                const inst = (u.instances || []).find((i) => String(i.instanceId) === id);
                return {
                  instanceId: id,
                  hostName: e?.hostName || inst?.hostname || u.epmMachineName || `Endpoint #${id}`,
                  privateIp: e?.privateIp || "",
                  publicIp: e?.publicIp || u.epmMachinePublicIp || "",
                  lastActive: unixOrNull(e?.lastActive) ?? unixOrNull(inst?.lastActive),
                  // Live and reached through a gateway: where rotation can be set.
                  gatewayManaged: !!e?.jumpServerId && !endpointLifecycle(e?.status),
                };
              });
              // The most recent of its endpoints' last-active times.
              const actives = endpointsOfRow.map((e) => e.lastActive).filter((t) => t != null);
              return {
                ...u,
                endpoints: endpointsOfRow,
                lastActive: actives.length ? Math.max(...actives) : null,
              };
            });
            setEpmUsers(enriched);
          })
          .catch(() => {
            // Enrichment failures don't block the main list.
          });
      })
      .catch((err) => {
        setIsLoading(false);
        if (
          err?.response?.data?.message === "Invalid credentials" ||
          err?.response?.status === 401
        ) {
          logOutUser();
        } else {
          notification.error({ content: "Unable to fetch endpoint users" });
        }
      });
  };

  // The Endpoint filter's options: every endpoint, from the same list the
  // table's endpoint column uses. (It used tenants/listEntity, which 404s.)
  const fetchInstance = () => {
    listInstances({
      onSuccess: (rows) =>
        setEndpointData(
          (rows || []).map((r) => ({ id: r.instanceId, name: r.hostName, address: r.privateIp || r.publicIp || "" })),
        ),
      onFailure: () => {},
    });
  };

  useEffect(() => {
    fetchEPMUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex, debouncedSearch, endpointFilterId]);

  useEffect(() => {
    fetchInstance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Pagination math ----

  // ---- Per-row kebab menu ----
  const menuForUser = (user) => {
    // Reveal for each endpoint the account is rotated on, named by host when
    // the account is on more than one. Rotate now is in the Password rotation
    // dialog.
    const lines = isAdmin ? pwLines(user) : [];
    const onHost = (l) => (lines.length > 1 ? ` · ${l.hostName}` : "");
    const revealItems = lines
      .filter((l) => l.pw && l.pw.revealable)
      .map((l) => ({
        key: `reveal-${l.instanceId}`,
        danger: true,
        icon: <KeyRound size={13} />,
        label: (
          <span>
            Reveal · <span style={{ fontSize: 11, opacity: 0.75 }}>break-glass</span>
            {onHost(l)}
          </span>
        ),
        onClick: () => setRevealing(l.pw),
      }));
    // Password rotation… on each gateway-managed endpoint, for an account
    // with a password (or rotated already; shown when that isn't known).
    const rotationEps = isAdmin && rotatable(user.userName) ? lines.filter((l) => l.gatewayManaged) : [];
    const rotationItems = rotationEps
      .filter((l) => !l.pw || l.pw.policyId || l.pw.status !== "no_password")
      .map((l, _, all) => ({
        key: `rotation-${l.instanceId}`,
        icon: <CalendarClock size={13} />,
        label: `Password rotation…${all.length > 1 ? ` · ${l.hostName}` : ""}`,
        onClick: () => openRotation(l.instanceId, l.hostName, user.userName),
      }));
    return {
      items: [
        {
          key: "session-recordings",
          label: (
            <Link
              to={`/pam/jumpServer-sessionRecordings?epmUser=${user?.userName}`}
            >
              View Session Recordings
            </Link>
          ),
        },
        ...rotationItems,
        ...(revealItems.length ? [{ type: "divider" }, ...revealItems] : []),
      ],
    };
  };

  // ---- Endpoint Select options memoized ----
  const endpointOptions = useMemo(
    () =>
      endpointData.map((ep) => ({
        value: ep.id,
        label: ep.address && ep.address !== ep.name ? `${ep.name} (${ep.address})` : ep.name,
      })),
    [endpointData]
  );

  const userDetailState = (user) => {
    localStorage.removeItem("details");
    localStorage.setItem(
      "details",
      JSON.stringify({
        epmUsername: user.userName,
        epmUserId: user.userId,
        PublicIp: user.epmMachinePublicIp,
        HostName: user.epmMachineName,
      }),
    );
  };

  const columns = [
    {
      title: "User",
      key: "userName",
      // Leads with a link to the user's detail page, the way the group name
      // does on Endpoint Groups; small tags follow.
      render: (_, user) => {
        const m = rowInstanceIds(user)
          .map((id) => managed.get(accountKey(id, user.userName)))
          .find(Boolean);
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", minWidth: 0 }}>
            <Link
              to="/pam/endpointuserDetails"
              state={{
                epmUsername: user.userName,
                epmUserId: user.userId,
                PublicIp: user.epmMachinePublicIp,
                HostName: user.epmMachineName,
              }}
              onClick={() => userDetailState(user)}
              style={{ fontWeight: 500, color: AN_COLORS.ink }}
              title={user.userName}
            >
              {user.userName || "—"}
            </Link>
            {/* `source` is the account's user_source: "gateway" for accounts
                the gateway's scan found, "manual" for ones typed in. Others
                (agent-reported) carry no tag. */}
            {(user.source === "gateway" || user.source === "manual") && <SourceTag source={user.source} />}
            {m && (
              // expiresAt is null for an added user that never expires.
              <Tooltip
                title={`Added by Authnull${m.note ? ` · ${m.note}` : ""} — ${
                  m.expiresAt ? `deleted from the machine on ${moment(m.expiresAt).format("D MMM YYYY, HH:mm")}` : "never expires"
                }`}
              >
                <Tag color="geekblue" style={{ marginInlineEnd: 0, fontSize: 11 }}>
                  {m.expiresAt ? `Managed · expires ${moment(m.expiresAt).format("D MMM")}` : "Managed · never expires"}
                </Tag>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      // The machine(s) this account lives on; addresses in the tooltip.
      title: "Endpoint",
      key: "endpoint",
      render: (_, u) =>
        (u.endpoints || []).length ? (
          <div style={{ display: "grid", gap: 2 }}>
            {u.endpoints.map((e) => {
              const addr = [e.privateIp, e.publicIp].filter(Boolean).join(" · ");
              return (
                <Tooltip key={e.instanceId} title={addr || undefined}>
                  <span style={{ fontSize: 12.5, fontWeight: 500, color: AN_COLORS.ink, whiteSpace: "nowrap" }}>{e.hostName}</span>
                </Tooltip>
              );
            })}
          </div>
        ) : (
          <MonoCell>{u.epmMachineName}</MonoCell>
        ),
    },
    ...(isAdmin
      ? [
          {
            title: "Rotation",
            key: "rotation",
            render: (_, u) => (
              <PerEndpoint
                lines={pwLines(u)}
                render={(pw, l) => (
                  <RotationCell
                    pw={pw}
                    rot={l?.rot}
                    onOpenSetup={openSetup}
                    onOpen={l && l.gatewayManaged && rotatable(u.userName) ? () => openRotation(l.instanceId, l.hostName, u.userName) : undefined}
                  />
                )}
              />
            ),
          },
        ]
      : []),
    {
      title: "Activity",
      key: "lastActive",
      render: (_, user) => {
        const a = activityOf(user.lastActive);
        return (
          <Tooltip title={a.title}>
            <span style={{ display: "inline-flex", whiteSpace: "nowrap" }}>
              <StatusPill tone={a.tone} status={a.label} label={a.label} />
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "",
      key: "action",
      width: 56,
      render: (_, user) => <RowActionMenu items={menuForUser(user).items} />,
    },
  ];

  const applied = [
    debouncedSearch && { key: "search", label: `Search: ${debouncedSearch}`, clear: () => setSearch("") },
    endpointFilterId && {
      key: "endpoint",
      label: `Endpoint: ${endpointOptions.find((o) => o.value === endpointFilterId)?.label || endpointFilterId}`,
      clear: () => setEndpointFilterId(""),
    },
    sortBy !== "default" && {
      key: "sort",
      label: `Sort: ${sortBy === "userName" ? "User name" : sortBy === "endpoint" ? "Endpoint" : sortBy}`,
      clear: () => setSortBy("default"),
    },
  ].filter(Boolean);

  return (
    <PageShell>
      {notificationContext}

      <PageHeader
        eyebrow="PAM · Targets"
        title="Endpoint Users"
        subtitle="Local accounts on managed endpoints, and the credentials Authnull holds for them. Accounts stay current automatically — each endpoint is re-scanned daily."
        chips={<CountChip count={totalCount} label={totalCount === 1 ? "User" : "Users"} />}
      />

      <FilterCard
        columns="1.6fr 1fr 1fr"
        applied={applied}
        onClearAll={() => {
          setSearch("");
          setEndpointFilterId("");
          setSortBy("default");
        }}
        showing={sortedUsers.length}
        total={totalCount}
      >
        <FilterField label="Search user">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
            prefix={<Search size={14} color={AN_COLORS.muted} />}
            placeholder="ubuntu"
            style={{ height: 36 }}
          />
        </FilterField>

        <FilterField label="Endpoint">
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Any endpoint"
            value={endpointFilterId || undefined}
            onChange={(v) => setEndpointFilterId(v ?? "")}
            options={endpointOptions}
            style={{ width: "100%", height: 36 }}
          />
        </FilterField>

        <FilterField label="Sort">
          <Select
            value={sortBy}
            onChange={setSortBy}
            style={{ width: "100%", height: 36 }}
            options={[
              { value: "default", label: "Default" },
              { value: "userName", label: "User name" },
              { value: "endpoint", label: "Endpoint" },
            ]}
          />
        </FilterField>
      </FilterCard>

      <TableCard>
        <Table
          {...anTableProps}
          columns={columns}
          dataSource={sortedUsers}
          rowKey={(u) => `${u.userId}-${u.epmMachineId}-${u.id}`}
          loading={isLoading}
          scroll={{ x: "max-content" }}
          locale={tableEmpty(isLoading, applied.length ? "No endpoint users match your filters." : "No endpoint users found.")}
          pagination={{
            current: pageIndex,
            pageSize: PAGE_SIZE,
            total: pageFull ? Math.max(totalCount, pageIndex * PAGE_SIZE + 1) : totalCount,
            showSizeChanger: false,
            onChange: setPageIndex,
          }}
        />
      </TableCard>

      <Modal
        open={!!rotTarget?.loading}
        title={rotTarget ? `Password rotation · ${rotTarget.account}` : ""}
        footer={null}
        width={360}
        onCancel={closeRotation}
        destroyOnClose
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: AN_COLORS.ink2, padding: "8px 0" }}>
          <Spin size="small" /> Reading the account's rotation…
        </div>
      </Modal>

      <AccountRotationModal
        open={!!rotTarget && !rotTarget.loading}
        instanceId={rotTarget?.endpointId}
        account={rotTarget?.account}
        hostname={rotTarget?.hostName}
        rotation={rotTarget?.rotation}
        defaultIntervalDays={rotTarget?.defaultIntervalDays || 30}
        onClose={closeRotation}
        onRotated={() => {
          const t = rotTarget;
          if (t) notification.success({ content: `Rotation of ${t.account}@${t.hostName || `endpoint #${t.endpointId}`} queued` });
          pollStart.current = 0;
          setPwTick((x) => x + 1);
        }}
        onSaved={(rotation) => {
          const t = rotTarget;
          if (t) {
            notification.success({ content: `Rotation ${rotation?.enabled ? "on" : "off"} for ${t.account} on ${t.hostName}` });
            setRotInfo((m) => new Map(m).set(accountKey(t.endpointId, t.account), { ...(m.get(accountKey(t.endpointId, t.account)) || {}), ...rotation, own: true, profile: "" }));
          }
          closeRotation();
          pollStart.current = 0;
          setPwTick((x) => x + 1);
        }}
      />

      <RevealPasswordModal
        open={!!revealing}
        endpointId={revealing?.endpointId}
        account={revealing?.account}
        hostname={revealing?.hostname}
        onClose={() => {
          setRevealing(null);
          setPwTick((x) => x + 1);
        }}
      />
    </PageShell>
  );
};

export default React.memo(EndpointUsers);
